import React from 'react'
import ResponsiveEmbed from 'react-responsive-embed'

const getYouTubeVideoId = (videoUrl) => {
    if (!videoUrl || typeof videoUrl !== 'string') {
        return null
    }

    const matchers = [
        /youtu\.be\/([^?&/]+)/i,
        /youtube\.com\/watch\?v=([^?&/]+)/i,
        /youtube\.com\/embed\/([^?&/]+)/i,
        /youtube\.com\/v\/([^?&/]+)/i,
        /youtube\.com\/shorts\/([^?&/]+)/i,
        /youtube-nocookie\.com\/embed\/([^?&/]+)/i
    ]

    for (const matcher of matchers) {
        const match = videoUrl.match(matcher)
        if (match && match[1]) {
            return match[1]
        }
    }

    return null
}

const LazyYouTubeEmbed = ({
    videoUrl,
    inEditMode = false,
    className = '',
    thumbnailAltText = 'Video preview image',
    playButtonLabel = 'Play video'
}) => {
    const [hasActivatedVideo, setHasActivatedVideo] = React.useState(false)

    React.useEffect(() => {
        setHasActivatedVideo(false)
    }, [videoUrl])

    const youTubeVideoId = React.useMemo(() => getYouTubeVideoId(videoUrl), [videoUrl])
    const isYouTubeVideo = Boolean(youTubeVideoId)
    const shouldRenderIframe = inEditMode || hasActivatedVideo || !isYouTubeVideo
    const shouldAutoplay = hasActivatedVideo && isYouTubeVideo && !inEditMode

    const buildAutoplayUrl = React.useCallback((url) => {
        if (!url) {
            return url
        }

        if (url.includes('autoplay=')) {
            return url
        }

        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}autoplay=1`
    }, [])

    if (!videoUrl) {
        return null
    }

    if (shouldRenderIframe) {
        const embedSource = shouldAutoplay ? buildAutoplayUrl(videoUrl) : videoUrl

        return (
            <div className={className}>
                <ResponsiveEmbed
                    loading='lazy'
                    src={embedSource}
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                    allowFullScreen
                />
            </div>
        )
    }

    const thumbnailUrl = `https://i.ytimg.com/vi/${youTubeVideoId}/hqdefault.jpg`

    const handleActivateVideo = () => {
        setHasActivatedVideo(true)
    }

    return (
        <div className={className}>
            <button
                type='button'
                aria-label={playButtonLabel}
                className='relative block w-full overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
                onClick={handleActivateVideo}
            >
                <div className='relative h-0 w-full' style={{ paddingBottom: '56.25%' }}>
                    <img
                        src={thumbnailUrl}
                        alt={thumbnailAltText}
                        loading='lazy'
                        className='absolute inset-0 h-full w-full object-cover'
                    />
                    <span className='absolute inset-0 flex items-center justify-center transition-colors hover:bg-black/40'>
                        <span className='inline-flex items-center justify-center drop-shadow-[0_12px_24px_rgba(0,0,0,0.45)]'>
                            <svg
                                width='68'
                                height='48'
                                viewBox='0 0 68 48'
                                className='h-12 w-auto'
                                role='img'
                                aria-hidden='true'
                            >
                                <path
                                    className='ytp-large-play-button-bg'
                                    fill='#f00'
                                    d='M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z'
                                />
                                <path d='M 45,24 27,14 27,34' fill='#fff' />
                            </svg>
                        </span>
                    </span>
                </div>
            </button>
        </div>
    )
}

export default LazyYouTubeEmbed
